import type { z } from "zod";
import type { Frame, JsonObject, JsonValue, Point, SceneNode } from "./document";
import type { Rect } from "./geometry";
import type { GraphicNode } from "./graphic-tree";

export type ComponentRenderTarget = "screen" | "print";

export type ComponentAnchorMap = Readonly<Record<string, Point>>;

export type HitShape =
  | {
      type: "rect";
      rect: Rect;
    }
  | {
      type: "circle";
      centre: Point;
      radius: number;
    }
  | {
      type: "polygon";
      points: Point[];
    };

export type ComponentCompileDiagnostic = {
  severity: "warning" | "error";
  code: string;
  message: string;
};

export type ComponentCompileContext<Props = unknown> = {
  node: SceneNode;
  frame: Readonly<Frame>;
  props: Props;
  variant?: string;
  style: Readonly<JsonObject>;
  tokens: Readonly<Record<string, JsonValue>>;
  target: ComponentRenderTarget;
};

export type ComponentCompileResult = {
  graphics: GraphicNode[];
  anchors?: ComponentAnchorMap;
  layoutBounds?: Rect;
  visualBounds?: Rect;
  hitShape?: HitShape;
  diagnostics?: ComponentCompileDiagnostic[];
};

export type ComponentDefinition<Props = unknown> = {
  type: string;
  propertySchema: z.ZodType<Props>;
  compile: (context: ComponentCompileContext<Props>) => ComponentCompileResult;
};

type ErasedComponentDefinition = {
  type: string;
  propertySchema: z.ZodType<unknown>;
  compile: (context: ComponentCompileContext<unknown>) => ComponentCompileResult;
};

export function defineComponent<Props>(
  definition: ComponentDefinition<Props>,
): ComponentDefinition<Props> {
  return definition;
}

export class ComponentRegistry {
  readonly #definitions = new Map<string, ErasedComponentDefinition>();

  register<Props>(definition: ComponentDefinition<Props>) {
    if (this.#definitions.has(definition.type)) {
      throw new Error(`Component '${definition.type}' is already registered.`);
    }
    this.#definitions.set(definition.type, definition as ErasedComponentDefinition);
    return this;
  }

  get(type: string): ErasedComponentDefinition | undefined {
    return this.#definitions.get(type);
  }

  has(type: string) {
    return this.#definitions.has(type);
  }

  list() {
    return [...this.#definitions.keys()].sort();
  }
}
